import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Text,
  Button,
  Flex,
  IconButton,
  Skeleton,
  VStack,
  Select,
  HStack,
  Badge,
} from '@chakra-ui/react';
import {
  RiSkipBackLine,
  RiSkipForwardLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCalendarEventLine,
} from 'react-icons/ri';
import Header from '../header/Header';
import EventCard from './eventCard';
import CreateEventModal from '../../components/modal/CreateEventModal';
import { useDisclosure } from '@chakra-ui/hooks';
import { useData } from '../../data';
import CountryFilter from './CountryFilter';

const LoadingSkeleton = () => {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6} mt={4}>
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Box
          key={item}
          borderWidth="1px"
          borderRadius="lg"
          overflow="hidden"
          p={4}
          bg="white"
          boxShadow="md"
        >
          {/* Image/cover skeleton */}
          <Skeleton height="180px" mb={4} borderRadius="md" />

          {/* Title skeleton */}
          <Skeleton height="24px" width="70%" mb={2} borderRadius="sm" />

          {/* Subtitle skeleton */}
          <Skeleton height="18px" width="50%" mb={3} borderRadius="sm" />

          {/* Tags/Badges skeleton */}
          <HStack spacing={2} mb={3}>
            <Skeleton height="20px" width="60px" borderRadius="full" />
            <Skeleton height="20px" width="40px" borderRadius="full" />
          </HStack>

          {/* Description skeleton */}
          <Skeleton height="14px" width="90%" mb={1} borderRadius="sm" />
          <Skeleton height="14px" width="80%" mb={3} borderRadius="sm" />

          {/* Button skeleton */}
          <Skeleton height="36px" width="100px" borderRadius="md" />
        </Box>
      ))}
    </SimpleGrid>
  );
};

const EmptyState = ({ tabIndex }) => {
  return (
    <VStack spacing={4} py={8} px={4} textAlign="center">
      <Box as={RiCalendarEventLine} size="64px" color="gray.400" />
      <Text fontSize="xl" fontWeight="bold" color="gray.600">
        No Events Found
      </Text>
      <Text color="gray.500" maxW="md">
        {tabIndex === 0
          ? 'There are no events available at the moment. Check back later for new events!'
          : "You haven't created any events yet. Click the 'Create Event' button to get started!"}
      </Text>
    </VStack>
  );
};

const Events = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [sortOrder, setSortOrder] = useState('earliest');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const pageSize = 20;

  // Build API URL with filters
  const buildApiUrl = (endpoint) => {
    const baseUrl = `${endpoint}?page_no=${page}&page_size=${pageSize}&sort_by=${sortOrder === 'earliest' ? 'ASC' : 'DESC'}`;

    if (selectedCountries.length > 0) {
      const countryParams = selectedCountries.join(',');
      return `${baseUrl}&countries=${countryParams}`;
    }

    return baseUrl;
  };

  const {
    data: AllEventsData,
    isLoading: isLoadingAllEvents,
    refetch: refetchEvents,
  } = useData(buildApiUrl('events'), 30000, {}, 1, true);

  const {
    data: MyEventsData,
    isLoading: isLoadingMyEvents,
    refetch: refetchMyEvents,
  } = useData(buildApiUrl('events/mine'), 30000, {}, 1, false);

  const {
    isOpen: openCreateEventModal,
    onOpen: onOpenCreateEventModal,
    onClose: onCloseCreateEventModal,
  } = useDisclosure();

  useEffect(() => {
    if (tabIndex === 0) {
      setEvents(AllEventsData?.data?.events || []);
      setTotalEvents(AllEventsData?.data?.pagination?.total);
    } else {
      setEvents(MyEventsData?.data?.events || []);
      setTotalEvents(MyEventsData?.data?.pagination?.total);
    }

    setTotalPages(AllEventsData?.data?.pagination?.total_pages);
  }, [MyEventsData, AllEventsData, tabIndex]);

  useEffect(() => {
    if (tabIndex === 0) {
      refetchEvents();
    } else {
      refetchMyEvents();
    }
  }, [sortOrder, selectedCountries]);

  // Handle tab change
  const handleTabsChange = (index) => {
    if (index === 0) {
      refetchEvents();
    } else {
      refetchMyEvents();
    }

    setTabIndex(index);
    setPage(1);
  };

  const isLoading = isLoadingAllEvents || isLoadingMyEvents;

  return (
    <Box className="bg-gray-100 w-full min-h-screen">
      <Header title={'Events'} />

      <Box className="w-full p-4">
        <Box className="flex flex-col bg-white" px={{ base: 4, md: 6 }} py={4}>
          <Flex
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            gap={4}
            className="md:mb-6 mb-4"
          >
            <HStack
              className="space-x-2 md:space-x-4"
              align="center"
              flexWrap="wrap"
            >
              {/* <Text
                fontWeight="semibold"
                fontSize="md"
                color="gray.700"
                className="md:text-sm text-xs whitespace-nowrap"
              >
                Sort by:
              </Text> */}

              <Box className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <Select
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                  }}
                  defaultValue="earliest"
                  size="md"
                  w={{ base: 'full', sm: '200px', md: '220px' }}
                  bg="white"
                  borderColor="gray.300"
                  fontWeight="medium"
                  _hover={{ borderColor: '#134A70' }}
                  _focus={{
                    borderColor: '#134A70',
                    boxShadow: '0 0 0 1px #134A70',
                  }}
                  className="md:text-sm text-xs"
                >
                  <option value="earliest">&#8593; Earliest to Latest</option>
                  <option value="latest">&#8595; Latest to Earliest</option>
                </Select>

                <CountryFilter
                  selectedCountries={selectedCountries}
                  setSelectedCountries={setSelectedCountries}
                  setPage={setPage}
                />
              </Box>

              <Badge
                colorScheme="green"
                variant="solid"
                fontWeight="bold"
                fontSize="sm"
                px={3}
                py={2}
                borderRadius="md"
                letterSpacing="wide"
                display={{ base: 'none', md: 'inline-flex' }}
                className="text-sm"
              >
                {totalEvents} EVENTS
              </Badge>
            </HStack>

            <Button
              size="md"
              onClick={onOpenCreateEventModal}
              bg="#134A70"
              color="white"
              _hover={{ bg: '#134A70' }}
              fontWeight="bold"
              px={6}
              className="w-fit self-end mt-2 md:mt-0"
            >
              Create Event
            </Button>
          </Flex>

          <Tabs
            index={tabIndex}
            onChange={handleTabsChange}
            variant="enclosed"
            bg="white"
            w="full"
            borderRadius="md"
            boxShadow="sm"
            className="w-full mt-2 md:mt-4"
          >
            <TabList>
              <Tab
                _selected={{
                  borderBottom: '4px solid #134A70',
                  color: '#134A70',
                  fontWeight: 'bold',
                }}
                borderBottom="4px solid transparent"
              >
                All Events
              </Tab>
              <Tab
                _selected={{
                  borderBottom: '4px solid #134A70',
                  color: '#134A70',
                  fontWeight: 'bold',
                }}
                borderBottom="4px solid transparent"
              >
                My Events
              </Tab>
            </TabList>
            <TabPanels>
              {[0, 1].map((tab) => (
                <TabPanel
                  key={tab}
                  px={0}
                  className="overflow-y-auto md:max-h-[calc(100vh-300px)] hide-scroll"
                >
                  {isLoading ? (
                    <LoadingSkeleton />
                  ) : events?.length === 0 ? (
                    <EmptyState tabIndex={tabIndex} />
                  ) : (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-6">
                      {events?.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          tabIndex={tabIndex}
                          refetchMyEvents={refetchMyEvents}
                          refetchEvents={refetchEvents}
                        />
                      ))}
                    </div>
                  )}

                  {events?.length > 0 && totalPages > 1 && (
                    <Flex justify="center" mt={6} gap={2} pb={4}>
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(1)}
                        isDisabled={page === 1}
                        icon={<RiSkipBackLine />}
                        aria-label="First page"
                      />
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(page - 1)}
                        isDisabled={page === 1}
                        icon={<RiArrowLeftSLine />}
                        aria-label="Previous page"
                      />
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          variant={page === i + 1 ? 'solid' : 'outline'}
                          bg={page === i + 1 ? '#134A70' : 'transparent'}
                          color={page === i + 1 ? 'white' : '#134A70'}
                          borderColor="#134A70"
                          onClick={() => setPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(page + 1)}
                        isDisabled={page === totalPages}
                        icon={<RiArrowRightSLine />}
                        aria-label="Next page"
                      />
                      <IconButton
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        color="#134A70"
                        borderColor="#134A70"
                        onClick={() => setPage(totalPages)}
                        isDisabled={page === totalPages}
                        icon={<RiSkipForwardLine />}
                        aria-label="Last page"
                      />
                    </Flex>
                  )}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </Box>

      <CreateEventModal
        isOpen={openCreateEventModal}
        onClose={onCloseCreateEventModal}
        tabIndex={tabIndex}
        refetchEvents={refetchEvents}
        refetchMyEvents={refetchMyEvents}
      />
    </Box>
  );
};

export default Events;
